<template>
    <div class="project-settings">
        <design-2021-apprentice-multistep-form-wizard-form-header-component
            :description="description"
            :title="$t('group.create.settings')">
        </design-2021-apprentice-multistep-form-wizard-form-header-component>

        <div
            class="errors"
            v-if="hasError">
            <b-alert
                class="project-settings-error"
                v-for="(error, index) in errors"
                v-if="error.error"
                :key="`error-${index}`"
                show
                variant="danger">
                {{ error.message }}
            </b-alert>
        </div>

        <b-form-group
            class="project-settings-course-group"
            :label="$t('group.create.course')"
            v-if="!!courses.length"
            label-for="settings-course-select">
            <template v-slot:label>{{ $t('group.create.course') }}: <span class="required">*</span></template>
            <b-form-select
                class="apprentice-primary-select"
                id="settings-course-select"
                v-model="selectedCourse"
                :options="courses"
                text-field="text"
                value-field="value"
                @change="updateCourse">
            </b-form-select>
        </b-form-group>

        <b-form-group
            class="project-settings-custom-banner-group"
            :label="$t('group.create.custom_banner') + ':'"
            label-for="settings-custom-banner-upload">
            <b-form-file
                class="apprentice-file-upload project-settings-custom-banner"
                id="settings-custom-banner-upload"
                v-model="file"
                ref="bannerUpload"
                :placeholder="$t('group.create.custom_banner')"
                accept="image/jpeg"
                @input="updateBannerSetting">
            </b-form-file>
        </b-form-group>

        <div class="seasonal-dates">
            <b-form-group
                class="project-settings-season-group"
                :label="$t('group.create.school_year')"
                label-for="settings-season-select">
                <template v-slot:label> {{ $t('group.create.school_year') }}: <span class="required">*</span></template>
                <b-form-select
                    class="apprentice-primary-select"
                    id="settings-season-select"
                    v-model="selectedSeason"
                    :options="seasons"
                    @input="updateSeason">
                </b-form-select>
            </b-form-group>

            <b-form-group
                class="project-settings-season-start-group"
                :label="$t('group.create.start_date')"
                label-for="settings-season-start-datepicker">
                <template v-slot:label>{{ $t('group.create.start_date') }}: <span class="required">*</span></template>
                <b-form-invalid-feedback>{{ $t('group.create.choose_start_date') }}</b-form-invalid-feedback>
                <b-form-datepicker
                    class="apprentice-date-picker season-start-date"
                    id="settings-season-start-datepicker"
                    v-model="settings.start_date"
                    :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
                    :aria-controls="$t('group.create.choose_date')"
                    :label-no-date-selected="$t('group.create.no_date_selected')"
                    label-reset-button="reset"
                    locale="nl"
                    menu-class="datedropdown"
                    nav-button-variant="primary"
                    now-button
                    reset-button
                    right
                    start-weekday="1"
                    today-variant="primary"
                    @input="updateSeasonStart">
                </b-form-datepicker>
            </b-form-group>

            <b-form-group
                class="project-settings-season-start-time-group"
                :label="$t('group.create.start_time')">
                <template v-slot:label
                    >{{ $t('group.create.project_visible_from') }}:<span class="required">*</span>
                </template>

                <b-form-invalid-feedback>
                    {{ $t('group.create.project_visible_explanation') }}
                </b-form-invalid-feedback>

                <design-2021-apprentice-timepicker-component
                    v-model="settings.start_time"
                    :hide-clear-button="true"
                    @blur="onEmptyTimeSetToDefault"
                    @input="updateSeasonStartTime">
                </design-2021-apprentice-timepicker-component>
            </b-form-group>

            <b-form-group
                class="project-settings-season-end-group"
                label="Eind datum"
                label-for="settings-season-end-datepicker">
                <template v-slot:label>{{ $t('group.create.end_date') }}: <span class="required">*</span></template>
                <b-form-invalid-feedback>{{ $t('group.create.choose_start_date_first') }}</b-form-invalid-feedback>
                <b-form-datepicker
                    class="apprentice-date-picker season-end-date"
                    id="settings-season-end-datepicker"
                    v-model="settings.end_date"
                    :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
                    :initial-date="getMinDate"
                    :min="getNextDay"
                    :aria-controls="$t('group.create.choose_date')"
                    :label-no-date-selected="$t('group.create.no_date_selected')"
                    label-reset-button="reset"
                    locale="nl"
                    menu-class="datedropdown"
                    nav-button-variant="primary"
                    now-button
                    reset-button
                    right
                    start-weekday="1"
                    today-variant="primary"
                    @input="updateSeasonEnd">
                </b-form-datepicker>
            </b-form-group>
        </div>

        <b-form-group
            class="project-settings-editor-type-group"
            v-if="settings.type !== 4"
            :label="$t('common.editor_type')"
            label-for="settings-editor-type-select">
            <template v-slot:label>{{ $t('group.create.editor_type') }}: <span class="required">*</span></template>
            <b-form-select
                class="apprentice-primary-select"
                id="settings-editor-type-select"
                v-model="selectedEditorType"
                :options="editorTypes"
                @input="updateEditorType">
            </b-form-select>
        </b-form-group>
    </div>
</template>

<script>
import $ from 'jquery';
import moment from 'moment';

function foreReload(callback) {
    requestAnimationFrame(() => {
        requestAnimationFrame(callback);
    });
}

export default {
    name: 'design-2021-apprentice-multistep-wizard-project-settings-step-component',
    props: {
        value: Object,
        seasonsIn: {
            type: Array,
            required: true,
        },
        currentSeasonId: {
            type: Number,
            required: true,
        },
        coursesIn: {
            type: Array,
            required: true,
        },
        editorTypeSetting: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            description: this.$t('group.fill_in_all_fields_for_project'),
            settings: this.value,
            selectedCourse: this.value.course && Object.keys(this.value.course).length > 0 ? this.value.course : null,
            selectedSeason:
                this.seasonsIn.filter((season) => season.id === this.currentSeasonId).length !== 0
                    ? this.seasonsIn.filter((season) => season.id === this.currentSeasonId).shift().id
                    : null,
            selectedEditorType: this.value.editor_type ? this.value.editor_type : 'sp',
            editorTypes: [
                { value: null, text: this.$t('group.choose_editor'), disabled: true },
                { value: 'oo', text: 'Only Office' },
                { value: 'moo', text: 'Microsoft Office Online' },
                { value: 'sp', text: 'Office 365' },
                { value: 'gd', text: 'Google Docs' },
            ],
            file: null,
            hasError: false,
            errors: {
                course: {
                    error: false,
                    message: this.$t('group.no_course_selected'),
                },
                season: {
                    error: false,
                    message: this.$t('group.no_school_year_selected'),
                },
                season_start_date: {
                    error: false,
                    message: this.$t('group.school_year_start_date_required'),
                },
                season_time: {
                    error: false,
                    message: this.$t('group.school_year_start_time_required'),
                },
                season_end_date: {
                    error: false,
                    message: this.$t('group.school_year_end_date_required'),
                },
                editor_type: {
                    error: false,
                    message: this.$t('group.no_editor_selected'),
                },
            },
        };
    },

    mounted() {
        if (this.selectedSeason !== '') {
            this.settings.season = this.seasonsIn.filter((season) => season.id === this.selectedSeason).shift();
        }

        if (this.editorTypeSetting !== '') {
            this.selectedEditorType = this.editorTypeSetting;
            this.updateEditorType(this.editorTypeSetting);
        }
    },

    computed: {
        courses() {
            if (this.coursesIn.length === 0) {
                return [];
            }

            let courses = this.coursesIn.map((course) => {
                return {
                    value: course.id,
                    text: course.name,
                };
            });

            courses.unshift({
                value: null,
                text: this.$t('group.choose_course'),
            });

            return courses;
        },

        seasons() {
            if (this.seasonsIn.length === 0) {
                return [];
            }

            let seasons = this.seasonsIn.map((season) => {
                return {
                    value: season.id,
                    text: season.season,
                };
            });

            seasons.unshift({
                value: null,
                text: this.$t('group.choose_school_year'),
                disabled: true,
            });

            return seasons;
        },

        getMinDate() {
            let initialDate = this.settings.start_date !== '' ? new Date(this.settings.start_date) : new Date();
            initialDate.setMonth(initialDate.getMonth() + 10);
            return initialDate;
        },

        getNextDay() {
            let initialDate = this.settings.start_date !== '' ? new Date(this.settings.start_date) : new Date();
            initialDate.setDate(initialDate.getDate() + 1);
            return initialDate;
        },
    },

    methods: {
        onEmptyTimeSetToDefault() {
            if (!this.settings.start_time) {
                this.settings.start_time = '00:00:00';
            }
        },

        updateCourse(courseId) {
            this.settings.course = this.coursesIn.filter((course) => course.id === courseId).shift();
            this.updateSettings();
        },

        updateSeason(seasonId) {
            this.settings.season = this.seasonsIn.filter((season) => season.id === seasonId).shift();
            this.updateSettings();
        },

        updateSeasonStart(seasonStart) {
            this.settings.start_date = seasonStart;
            this.updateSettings();
        },

        updateSeasonStartTime(seasonStartTime) {
            this.settings.start_time = seasonStartTime;
            this.updateSettings();
        },

        updateSeasonEnd(seasonEnd) {
            this.settings.end_date = seasonEnd;
            this.updateSettings();
        },

        updateEditorType(editorTypeId) {
            this.settings.editor_type = this.editorTypes.filter((type) => type.value === editorTypeId).shift();
            this.updateSettings();
        },

        updateBannerSetting(bannerFile) {
            this.settings.banner = bannerFile;
            this.updateSettings();
        },

        updateSettings() {
            this.$emit('input', this.settings);
        },

        async checkForErrors() {
            this.hasError = false;
            this.errors.course.error = false;
            this.errors.season.error = false;
            this.errors.editor_type.error = false;
            this.errors.season_start_date.error = false;
            this.errors.season_end_date.error = false;

            if (this.selectedCourse === null && !!this.courses.length) {
                this.hasError = true;
                this.errors.course.error = true;
            }

            if (this.selectedSeason === null) {
                this.hasError = true;
                this.errors.season.error = true;
            }

            if (this.settings.type !== 4 && this.selectedEditorType === null) {
                this.hasError = true;
                this.errors.editor_type.error = true;
            }

            if (this.settings.start_date === '') {
                this.hasError = true;
                this.errors.season_start_date.error = true;
            }

            if (this.settings.end_date === '') {
                this.hasError = true;
                this.errors.season_end_date.error = true;
            }

            const response = this.hasError;
            return await response;

            if (this.hasError) {
                Vue.nextTick(() => {
                    foreReload(() => {
                        const scrollPos = $(`.project-settings .errors`)[0].offsetTop - 30;
                        $('.main-content').animate({ scrollTop: scrollPos }, 500);
                    });
                });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
input,
select,
.apprentice-date-picker,
.apprentice-default-time-picker,
.apprentice-default-time-picker ::v-deep input {
    margin-bottom: 0;
}

.project-settings-custom-banner-group ::v-deep {
    > label + div {
        height: 51px;
    }
}

@media (min-width: map-get($grid-breakpoints, 'xxl')) {
    .seasonal-dates {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        column-gap: map-get($grid-gutter-spacing, 'fifteen');
        margin-bottom: $grid-gutter-width;

        > .form-group {
            flex: 1 1 calc(33% - 15px);
            margin-bottom: 0;
        }
    }
}
</style>
